import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { initializeApiConfig } from "./GLOBAL/config/apiConfig";
import { initializeOperator } from "./GLOBAL/redux/auth";
import {
  setIsAuthenticated,
  setEmail,
  setUsername,
} from "./GLOBAL/redux/slice/authSlice";
import { fetchProfile } from "./GLOBAL/redux/account";
import {
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
} from "./GLOBAL/constants/authApis";
import { refresh_token } from "./GLOBAL/constants/refreshToken";
import { DEFAULT_OPERATOR } from "./utils/operators";
import { STORAGE_KEYS } from "./utils/constants";

/**
 * AppWrapper — Initializes API config, operator, and auth state before
 * rendering the app.
 *
 * On mount:
 *   1. Set default operator in localStorage (9mobile)
 *   2. Call CRM landing endpoint to fetch dynamic Client API URL
 *   3. Restore auth state from localStorage (persistent login)
 *   4. If token is expired, attempt a silent refresh
 *   5. Fetch user profile if authenticated
 *   6. Once initialized, render children (App)
 */
const AppWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        // 1. Ensure operator is set in localStorage
        initializeOperator();

        // 2. Fetch dynamic Client API URL from CRM landing endpoint
        await initializeApiConfig(DEFAULT_OPERATOR.operator_uid);

        // 3. Restore auth state from localStorage
        const accessToken = getAccessToken();
        const refreshTokenValue = getRefreshToken();
        const wasAuthenticated =
          localStorage.getItem(STORAGE_KEYS.AUTHENTICATED) === "true";

        if (wasAuthenticated && (accessToken || refreshTokenValue)) {
          let isAuth = false;

          if (isTokenExpired()) {
            // 4. Token expired — attempt silent refresh
            if (refreshTokenValue) {
              try {
                await refresh_token();
                isAuth = true;
              } catch {
                // Refresh failed — user needs to log in again
                isAuth = false;
              }
            }
          } else {
            // Token still valid
            isAuth = true;
          }

          dispatch(setIsAuthenticated(isAuth));

          if (isAuth) {
            // Restore email/username from localStorage
            const storedEmail = localStorage.getItem(STORAGE_KEYS.EMAIL);
            const storedUsername = localStorage.getItem(STORAGE_KEYS.USERNAME);
            if (storedEmail) dispatch(setEmail(storedEmail));
            if (storedUsername) dispatch(setUsername(storedUsername));

            // 5. Fetch profile in background
            fetchProfile().catch(() => {});
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("AppWrapper initialization failed:", error);
        setIsInitialized(true); // Still render app with fallback URL
      }
    };
    initialize();
  }, [dispatch]);

  if (!isInitialized) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#0a0a0a",
          color: "#fafafa",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid rgba(255, 255, 255, 0.1)",
              borderTop: "4px solid #FC982B",
              borderRadius: "50%",
              width: "48px",
              height: "48px",
              animation: "mango-spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <div style={{ fontSize: "14px", color: "#a3a3a3" }}>
            Loading OpenPlay...
          </div>
          <style>{`@keyframes mango-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AppWrapper;
